<template>
    <div class="mt-2">
        <div>
            <h1 class="text-2xl font-bold mb-6 text-gray-800 dark:text-white flex items-center">
                Setores
            </h1>

            <!-- Busca -->
            <div class="mb-4">
                <input
                    v-model="search"
                    type="text"
                    placeholder="Buscar membro por nome ou email..."
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
            </div>

            <!-- Tabela de setores -->
            <table-component
                :loading="loading"
                :headers="[
                    'Nome do Setor',
                    ''
                ]"
            >
                <tr
                    v-for="sector in filteredSectors"
                    :key="sector.id"
                    class="hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                    @click="showMembers(sector)"
                >
                    <td class="table-cell-sm">
                        <div class="flex items-center">
                            <div class="ml-3">
                                <p class="text-gray-900 dark:text-white whitespace-no-wrap">
                                    {{ sector.name }}
                                </p>
                            </div>
                        </div>
                    </td>
                    <td class="table-cell-sm">
                        <div class="flex items-center" v-if="sector.leader_count > 0">
                            <template v-if="sector.users.length <= 10">
                                <img
                                    v-tooltip="user.name + ' - ' + user.email"
                                    v-for="user in sector.users"
                                    class="mr-1 h-8 w-8 rounded-full"
                                    :src="
                                    user.avatar ||
                                    'https://api.dicebear.com/7.x/initials/svg?backgroundColor=c0aede&seed=' +
                                        user.name
                                    "
                                    alt=""
                                />
                            </template>
                            <template v-else>
                                <img
                                    v-for="user in sector.users.slice(0, 9)"
                                    v-tooltip="user.name + ' - ' + user.email"
                                    class="mr-1 h-8 w-8 rounded-full"
                                    :src="
                                    user.avatar ||
                                    'https://api.dicebear.com/7.x/initials/svg?backgroundColor=c0aede&seed=' +
                                        user.name
                                    "
                                    alt=""
                                />
                                <div v-tooltip="'Existem mais membros'" class="flex items-center justify-center mr-1 h-8 w-8 rounded-full bg-gray-300 dark:bg-gray-600 text-xs font-bold">
                                    + {{ sector.users.length - 9 }}
                                </div>
                            </template>
                        </div>
                    </td>
                </tr>
                <tr v-if="filteredSectors.length === 0">
                    <td colspan="2" class="table-cell-sm text-center text-gray-500 dark:text-gray-400">
                        Nenhum setor encontrado.
                    </td>
                </tr>
            </table-component>
        </div>
        
        <!-- Modal para adicionar/editar setor -->
        <modal :open="showAddSectorModal || showEditSectorModal" @close="showAddSectorModal = false; showEditSectorModal = false">
            <div class="mb-4">
                <h3 class="text-lg font-medium text-gray-900 dark:text-white">
                    {{ showEditSectorModal ? 'Editar Setor' : 'Adicionar Setor' }}
                </h3>
                <div class="mt-4">
                    <div class="mb-4">
                        <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Nome do Setor</label>
                        <input 
                            type="text" 
                            id="name" 
                            v-model="currentSector.name" 
                            class="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            placeholder="Digite o nome do setor"
                        >
                    </div>
                </div>
            </div>
            
            <div class="mt-5 flex justify-end space-x-2">
                <button 
                    @click="showAddSectorModal = false; showEditSectorModal = false" 
                    class="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                    Cancelar
                </button>
                <button 
                    @click="saveSector" 
                    class="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                >
                    Salvar
                </button>
            </div>
        </modal>
        
        <!-- Modal de confirmação para excluir setor -->
        <modal :open="showDeleteConfirmModal" @close="showDeleteConfirmModal = false">
            <div class="mb-4 text-center">
                <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10 mb-4">
                    <svg class="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                </div>
                <h3 class="text-lg font-medium text-gray-900 dark:text-white">Excluir Setor</h3>
                <div class="mt-2">
                    <p class="text-sm text-gray-500 dark:text-gray-300">
                        Tem certeza que deseja excluir o setor "{{ currentSector.name }}"? Esta ação não pode ser desfeita.
                    </p>
                </div>
            </div>
            
            <div class="mt-5 flex justify-end space-x-2">
                <button 
                    @click="showDeleteConfirmModal = false" 
                    class="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                >
                    Cancelar
                </button>
                <button 
                    @click="confirmDeleteSector" 
                    class="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                >
                    Excluir
                </button>
            </div>
        </modal>
    </div>
</template>

<script>
import TableComponent from "../../components/layouts/table/TableComponent.vue";
import PaginationObject from "../../mixins/PaginationObject.vue";
import Modal from "../../components/layouts/Modal.vue";
import PermissionMixin from "../../mixins/PermissionMixin.vue";

export default {
    components: {
        TableComponent,
        Modal
    },
    mixins: [PaginationObject, PermissionMixin],
    data() {
        return {
            sectors: [],
            search: '',
            currentSector: {
                id: null,
                name: ''
            },
            showAddSectorModal: false,
            showEditSectorModal: false,
            showDeleteConfirmModal: false,
            loading: false,
            errors: {}
        }
    },

    computed: {
        filteredSectors() {
            if (!this.search) {
                return this.sectors;
            }

            const term = this.search.toLowerCase();

            return this.sectors.filter(sector =>
                sector.users?.some(user =>
                    user.name.toLowerCase().includes(term)
                    || user.email.toLowerCase().includes(term)
                )
            );
        }
    },
    
    mounted() {
        this.fetchSectors();
    },
    
    methods: {
        fetchSectors() {
            this.loading = true;
            axios.get('/api/sectors')
                .then(response => {
                    this.sectors = response.data.roles;
                    this.loading = false;
                })
                .catch(error => {
                    console.error('Erro ao carregar setores:', error);
                    this.loading = false;
                    this.$notify({
                        group: 'error',
                        title: 'Erro',
                        text: 'Erro ao carregar setores. Tente novamente mais tarde.'
                    }, 4000);
                });
        },
        
        editSector(sector) {
            this.currentSector = { ...sector };
            this.showEditSectorModal = true;
        },
        
        saveSector() {
            this.loading = true;
            
            // Determinar se é criação ou edição
            const isEdit = this.showEditSectorModal;
            const url = isEdit ? `/api/sectors/${this.currentSector.id}` : '/api/sectors';
            const method = isEdit ? 'put' : 'post';
            
            axios[method](url, {
                name: this.currentSector.name
            })
                .then(response => {
                    this.loading = false;
                    this.showAddSectorModal = false;
                    this.showEditSectorModal = false;
                    this.fetchSectors();
                    this.$notify({
                        group: 'success',
                        text: isEdit ? 'Setor atualizado com sucesso!' : 'Setor criado com sucesso!'
                    }, 4000);
                })
                .catch(error => {
                    this.loading = false;
                    console.error('Erro ao salvar setor:', error);
                    this.$notify({
                        group: 'error',
                        title: 'Erro',
                        text: error.response?.data?.message || 'Erro ao salvar setor. Tente novamente mais tarde.'
                    }, 4000);
                });
        },
        
        deleteSector(sector) {
            this.currentSector = { ...sector };
            this.showDeleteConfirmModal = true;
        },
        
        confirmDeleteSector() {
            this.loading = true;
            
            axios.delete(`/api/sectors/${this.currentSector.id}`)
                .then(response => {
                    this.loading = false;
                    this.showDeleteConfirmModal = false;
                    this.fetchSectors();
                    this.$notify({
                        group: 'success',
                        text: 'Setor excluído com sucesso!'
                    }, 4000);
                })
                .catch(error => {
                    this.loading = false;
                    this.showDeleteConfirmModal = false;
                    console.error('Erro ao excluir setor:', error);
                    this.$notify({
                        group: 'error',
                        title: 'Erro',
                        text: error.response?.data?.message || 'Erro ao excluir setor. Tente novamente mais tarde.'
                    }, 4000);
                });
        },
        
        showMembers(sector) {
            this.$router.push({ name: 'sector.members', params: { role_id: sector.id } });
        }
    }
}
</script> 